import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bell, BellOff, Compass } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Spinner } from "../components/common";

/* My Deal Alerts — every destination the member follows, with quick unfollow. */
export default function MyDealAlerts() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    document.title = "My Deal Alerts | TravelerPulse";
    api.get("/deal-alerts/mine").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  const unfollow = async (f) => {
    try {
      await api.post("/deal-alerts/toggle", { slug: f.slug, name: f.name, country: f.country });
      setItems((prev) => prev.filter((x) => x.slug !== f.slug));
      toast.success(`Deal alerts for ${f.name} turned off`);
    } catch {
      toast.error("Could not unfollow — try again");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10" data-testid="my-deal-alerts-page">
      <Link to="/travel-hub" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mb-4"
        data-testid="deal-alerts-back">
        <ArrowLeft className="w-4 h-4" /> My Travel Hub
      </Link>
      <h1 className="font-heading font-900 text-3xl sm:text-4xl flex items-center gap-2.5 mb-2">
        <Bell className="w-8 h-8 text-amber-500" /> My Deal Alerts
      </h1>
      <p className="text-sm text-muted-foreground mb-8">
        We check deals headlines daily and email you only when something new appears for a destination you follow.
      </p>

      {items === null ? <Spinner /> : items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 bg-zinc-50 p-10 text-center" data-testid="deal-alerts-empty">
          <Bell className="w-10 h-10 mx-auto text-zinc-400 mb-3" />
          <p className="font-heading font-700 text-lg">You don't follow any destinations yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Open any destination guide and tap "Get deal alerts" in its news section.
          </p>
          <Link to="/explore-places" className="inline-flex items-center gap-1.5 mt-4 px-5 py-2.5 bg-primary text-white text-sm font-700 hover:bg-blue-700">
            <Compass className="w-4 h-4" /> Explore destinations
          </Link>
        </div>
      ) : (
        <div className="space-y-2" data-testid="deal-alerts-list">
          {items.map((f) => (
            <div key={f.slug} className="flex items-center justify-between gap-3 border border-zinc-200 bg-white px-4 py-3"
              data-testid={`deal-alert-row-${f.slug.split("/").pop()}`}>
              <div className="min-w-0">
                <Link to={`/explore/${f.slug}`} className="font-heading font-800 hover:text-blue-700 hover:underline">
                  {f.name}
                </Link>
                <div className="text-xs text-muted-foreground">
                  {f.country}{f.created_at ? ` · following since ${String(f.created_at).slice(0, 10)}` : ""}
                </div>
              </div>
              <button onClick={() => unfollow(f)} data-testid={`deal-alert-unfollow-${f.slug.split("/").pop()}`}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-700 border border-zinc-300 text-zinc-600 hover:border-red-400 hover:text-red-600 transition-colors">
                <BellOff className="w-3.5 h-3.5" /> Unfollow
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
